import { Injectable, type Signal, signal, type WritableSignal } from '@angular/core';

type VisibilityGroupName = 'header-footer' | 'navigation-buttons';

interface VisibilityGroupData {
  elements: HTMLElement[];
  isVisible: WritableSignal<boolean>;
  timeout: ReturnType<typeof setTimeout> | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class ControlsVisibility {
  private static readonly ELEMENT_HIDE_DELAY = 5000;
  private static readonly START_INTERACTION_EVENTS = ['pointerdown', 'mouseenter', 'focusin'];
  private static readonly END_INTERACTION_EVENTS = ['pointerup', 'mouseleave', 'focusout'];

  private readonly groups: Record<VisibilityGroupName, VisibilityGroupData> = {
    'header-footer': {
      elements: [],
      isVisible: signal(true),
      timeout: undefined,
    },
    'navigation-buttons': {
      elements: [],
      isVisible: signal(true),
      timeout: undefined,
    },
  };

  get isHeaderFooterVisible(): Signal<boolean> {
    return this.groups['header-footer'].isVisible.asReadonly();
  }

  get isNavigationButtonsVisible(): Signal<boolean> {
    return this.groups['navigation-buttons'].isVisible.asReadonly();
  }

  constructor() {
    // Once the page is opened, all groups have to hide automatically after 5 seconds
    this.endInteraction('header-footer');
    this.endInteraction('navigation-buttons');
  }

  addElementsToGroup(groupName: VisibilityGroupName, elements: HTMLElement[]): void {
    this.groups[groupName].elements = [...this.groups[groupName].elements, ...elements];

    elements.forEach((element) => {
      ControlsVisibility.START_INTERACTION_EVENTS.forEach((eventName) => {
        element.addEventListener(eventName, () => {
          this.startInteraction(groupName);
        });
      });

      ControlsVisibility.END_INTERACTION_EVENTS.forEach((eventName) => {
        element.addEventListener(eventName, (event: Event) => {
          // Desktop devices should not hide controls after pointerup; only touch devices.
          if (
            eventName === 'pointerup' &&
            event instanceof PointerEvent &&
            event.pointerType === 'touch'
          ) {
            return;
          }
          this.endInteraction(groupName);
        });
      });
    });
  }

  startInteraction(groupName: VisibilityGroupName): void {
    const group = this.groups[groupName];
    clearTimeout(group.timeout);

    group.isVisible.set(true);
  }

  endInteraction(
    groupName: VisibilityGroupName,
    timeout = ControlsVisibility.ELEMENT_HIDE_DELAY,
  ): void {
    const group = this.groups[groupName];

    clearTimeout(group.timeout);

    group.timeout = setTimeout(() => {
      const hasFocus = group.elements.some((el) => el.contains(document.activeElement));
      if (!hasFocus) {
        group.isVisible.set(false);
      }
    }, timeout);
  }
}
